import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Copy, Mail, MessageSquare, Check, ExternalLink } from "lucide-react";

/**
 * ShareClientModal - Modal for sharing client portal links
 * 
 * Provides multiple ways to share the client portal link:
 * - Copy to clipboard
 * - Email
 * - SMS
 * - Direct preview
 */
const ShareClientModal = ({ isOpen, onClose, tripId, clientName }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  // Generate client portal link - send to Home page with tripId parameter
  const getClientPortalLink = () => {
    const clientPortalUrl = import.meta.env.VITE_CLIENT_PORTAL_URL || import.meta.env.VITE_CLIENT_APP_URL;
    if (!clientPortalUrl) {
      console.warn('VITE_CLIENT_PORTAL_URL and VITE_CLIENT_APP_URL are not set, sharing links may not work');
    }
    return `${clientPortalUrl}?tripId=${tripId}`; // Home page with tripId parameter
  };

  const clientPortalLink = getClientPortalLink();

  // Copy link to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(clientPortalLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy link. Please copy manually.');
    }
  };

  // Open email client
  const handleEmail = () => {
    const subject = encodeURIComponent(`Your Disney Itinerary from ${clientName || 'Your Agent'}`);
    const body = encodeURIComponent(
      `Hi!\n\n` +
      `Your personalized Disney client portal is ready! Access it here:\n\n` +
      `${clientPortalLink}\n\n` +
      `This portal includes:\n` +
      `✨ Your complete day-by-day itinerary\n` +
      `🎢 Ride recommendations and wait times\n` +
      `🍽️ Dining reservations\n` +
      `📱 Mobile-friendly access\n\n` +
      `Have a magical trip!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  // Open SMS
  const handleSMS = () => {
    const message = encodeURIComponent(
      `Your Disney client portal is ready! View it here: ${clientPortalLink}`
    );
    window.open(`sms:?body=${message}`);
  };

  // Preview the portal
  const handlePreview = () => {
    window.open(clientPortalLink, '_blank');
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999
      }}
    >
      <div className="mx-4 w-full max-w-md bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Share Client Portal
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 transition-colors hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="mb-4 text-sm text-gray-600">
            Share this personalized client portal with {clientName || 'your client'}:
          </p>

          {/* Link Display */}
          <div className="mb-6">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Client Portal Link
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={clientPortalLink}
                readOnly
                className="flex-1 px-3 py-2 text-sm bg-gray-50 rounded-md border border-gray-300 focus:outline-none"
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={handleCopy}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  copied
                    ? 'text-white bg-gray-600'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Sharing Options */}
          <div className="space-y-3">
            <p className="mb-2 text-sm font-medium text-gray-700">
              Share via:
            </p>

            {/* Email Button */}
            <button
              onClick={handleEmail}
              className="flex justify-center items-center px-4 py-3 space-x-2 w-full text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 transition-colors hover:bg-gray-50"
            >
              <Mail className="w-4 h-4" />
              <span>Email Client</span>
            </button>

            {/* SMS Button */}
            <button
              onClick={handleSMS}
              className="flex justify-center items-center px-4 py-3 space-x-2 w-full text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 transition-colors hover:bg-gray-50"
            >
              <MessageSquare className="w-4 h-4" />
              <span>Send via SMS</span>
            </button>

            {/* Preview Button */}
            <button
              onClick={handlePreview}
              className="flex justify-center items-center px-4 py-3 space-x-2 w-full text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 transition-colors hover:bg-gray-50"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Preview Portal</span>
            </button>
          </div>

          {/* Info Box */}
          <div className="p-4 mt-6 bg-gray-50 rounded-md border border-gray-200">
            <div className="flex items-start space-x-2">
              <span className="text-sm text-yellow-500">💡</span>
              <div className="text-xs text-gray-700">
                <span className="font-medium">Tip:</span> Clients can access their itinerary anytime, anywhere. The portal is mobile-friendly and includes real-time park information.
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 transition-colors hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareClientModal;

